// Simple localStorage-backed wishlist. Stores an array of car IDs.
import { useEffect, useState, useCallback } from "react";
import { api } from "./api";

const KEY = "guzz_wishlist";
const EVENT = "guzz_wishlist_change";
const CLIENT_KEY = "guzz_client_id";

function getClientId() {
  try {
    let id = localStorage.getItem(CLIENT_KEY);
    if (!id) {
      id = (crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36));
      localStorage.setItem(CLIENT_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

function trackServer(carId, action) {
  const client_id = getClientId();
  if (!client_id || !carId) return;
  api.post("/wishlist/track", { car_id: carId, action, client_id }).catch(() => {});
}

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function write(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent(EVENT));
  } catch {
    /* ignore quota */
  }
}

export function useWishlist() {
  const [ids, setIds] = useState(read());

  useEffect(() => {
    const sync = () => setIds(read());
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", (e) => {
      if (e.key === KEY) sync();
    });
    return () => window.removeEventListener(EVENT, sync);
  }, []);

  const toggle = useCallback((carId) => {
    const current = read();
    const wasIn = current.includes(carId);
    const next = wasIn
      ? current.filter((x) => x !== carId)
      : [carId, ...current];
    write(next);
    setIds(next);
    // Server-side aggregate counter (dedup by client_id) so admin can see demand.
    trackServer(carId, wasIn ? "remove" : "add");
    // First-ever add → suggest push activation once
    if (!wasIn) {
      try {
        const wasSuggested = localStorage.getItem("guzz_push_wishlist_prompted") === "1";
        const permOk = typeof Notification === "undefined" ? true : Notification.permission !== "denied";
        if (!wasSuggested && permOk && next.length >= 1) {
          localStorage.setItem("guzz_push_wishlist_prompted", "1");
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent("open-push-optin", { detail: { source: "wishlist" } }));
          }, 900);
        }
      } catch { /* ignore */ }
    }
    return next.includes(carId);
  }, []);

  const remove = useCallback((carId) => {
    const next = read().filter((x) => x !== carId);
    write(next);
    setIds(next);
    trackServer(carId, "remove");
  }, []);

  const has = useCallback((carId) => ids.includes(carId), [ids]);

  return { ids, count: ids.length, toggle, remove, has };
}
